import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { type PifEntry, beginPifVisit, endPifVisit, recordPifVisibilityChange } from "services/pifTiming";

/**
 * The patient intake form is `?patient_intake` on /patients/:patientId, a query-param state rather
 * than a route - the same pattern as the end-visit screens, and invisible to useScreenTimeTracking
 * for the same reason: its effect depends only on location.pathname, so a search-param transition
 * never re-runs it.
 *
 * Mounted once in Router next to useEndVisitTiming rather than inside PatientDetail. Removing the
 * param unmounts the form, and that unmount is the end of the measurement - a hook living inside
 * the component would have to survive its own teardown to report it.
 */

/**
 * Presence, not value. PatientDetail decides whether to render the form with
 * `params.has("patient_intake")`, so `?patient_intake=false` still shows the form - and anything
 * the provider can see is something worth measuring. Matching the app's own predicate rather than
 * a stricter one keeps the metric's population equal to the screen's.
 */
export function isPifOpen(search: string): boolean {
  try {
    return new URLSearchParams(search).has("patient_intake");
  } catch {
    return false;
  }
}

/** `?from=appointments` is set by the appointments dashboard; everything else came from the record. */
export function readPifEntry(search: string): PifEntry {
  try {
    return new URLSearchParams(search).get("from") === "appointments" ? "appointments" : "patient_detail";
  } catch {
    return "patient_detail";
  }
}

export function usePifTiming(): void {
  const location = useLocation();
  const search = location.search;
  const isPatientDetail = /^\/patients\/[^/]+$/.test(location.pathname);
  const open = isPatientDetail && isPifOpen(search);
  const openRef = useRef(false);

  useEffect(() => {
    openRef.current = open;

    if (!open) {
      // Also covers arriving anywhere else with a visit still in sessionStorage - a provider who
      // navigated away from the form in a previous page load abandoned it, and this is where that
      // gets reported.
      endPifVisit();
      return;
    }

    // Idempotent for the same patient, which matters because this effect re-runs on any change to
    // the query string - the form itself writes several unrelated params while open.
    beginPifVisit({
      entry: readPifEntry(search),
      patientId: location.pathname.split("/")[2] || undefined,
    });
  }, [open, search, location.pathname]);

  // Hidden-tab accounting. The PIF is left open across interruptions far more than the end-visit
  // screens are - a provider walks into the room mid-form - so the gap between wall clock and
  // active time is wider here, and active_ms is the figure worth reading.
  useEffect(() => {
    const onVisibility = () => recordPifVisibilityChange(document.visibilityState === "hidden");
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  // A tab closed on the form is an ordinary way to abandon it. Without this flush the measurement
  // is never emitted at all, which would bias every percentile toward the providers who finish.
  // pagehide rather than unload: unload is ignored when a page is eligible for the back/forward
  // cache, and never fires on mobile Safari.
  useEffect(() => {
    const onPageHide = () => {
      if (!openRef.current) return;
      endPifVisit();
    };
    window.addEventListener("pagehide", onPageHide);
    return () => window.removeEventListener("pagehide", onPageHide);
  }, []);
}
